<template>
  <b-modal
    id="history-kyc-modal"
    centered
    scrollable
    size="xl"
    title="Thông tin EKYC của merchant"
    title-class="font-weight-bold  w-100"
    @shown="onModalShown"
    @hidden="resetData"
  >
    <template #modal-header="{ close }">
      <h5 class="modal-title font-weight-bold text-center w-100">Thông tin EKYC của merchant</h5>
      <button type="button" aria-label="Close" class="close" @click="close()">×</button>
    </template>

    <template #modal-footer="{ cancel }">
      <div class="d-flex justify-content-end w-100">
        <b-button variant="secondary" class="mr-2 px-4" @click="cancel()">Đóng</b-button>
        <b-button
          variant="success"
          class="px-4"
          v-if="
            dataPermission &&
            dataPermission.includes('CNPS_CSTM_MERCHANT_EKYC_CONFIRM') &&
            merchantData?.statusKyc !== 'ACTIVE' &&
            !loading
          "
          @click="approveEkyc"
          >Duyệt</b-button
        >
      </div>
    </template>

    <div
      v-if="loading"
      class="text-center p-5 d-flex flex-column align-items-center justify-content-center"
      style="min-height: 500px"
    >
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Đang tải dữ liệu...</p>
    </div>

    <div v-else class="popup-ekyc-content" style="min-height: 500px">
      <!-- Section 1: Data Onboard -->
      <div class="card mb-3 shadow-sm border-0 bg-light-gray" style="padding: 5px !important">
        <div class="card-body p-3">
          <b-row>
            <!-- Left: Info -->
            <b-col md="7" class="border-right">
              <h6 class="font-weight-bold mb-3 border-left-primary pl-2">Thông tin khi onboard</h6>
              <div class="info-group">
                <b-row class="mb-2">
                  <b-col cols="4" class="text-muted">Tên merchant</b-col>
                  <b-col cols="8" class="font-weight-medium">{{ merchantData.merchantName }}</b-col>
                </b-row>
                <b-row class="mb-2">
                  <b-col cols="4" class="text-muted">Số CCCD</b-col>
                  <b-col cols="8" class="font-weight-medium">{{ merchantData.passport }}</b-col>
                </b-row>
                <b-row class="mb-2">
                  <b-col cols="4" class="text-muted">Ngày sinh</b-col>
                  <b-col cols="8" class="font-weight-medium">{{
                    merchantData.birthDayTime | formatDate
                  }}</b-col>
                </b-row>
                <b-row class="mb-2">
                  <b-col cols="4" class="text-muted">Giới tính</b-col>
                  <b-col cols="8" class="font-weight-medium">{{
                    merchantData.gender | formatGender
                  }}</b-col>
                </b-row>
                <b-row class="mb-2">
                  <b-col cols="4" class="text-muted">Địa chỉ thường trú</b-col>
                  <b-col cols="8" class="font-weight-medium">{{
                    merchantData.authoriserAddress1
                  }}</b-col>
                </b-row>
              </div>
            </b-col>
            <!-- Right: Images -->
            <b-col md="5">
              <h6 class="font-weight-bold mb-3 border-left-primary pl-2">Ảnh CCCD khi onboard</h6>
              <div class="d-flex flex-wrap" v-viewer>
                <div
                  v-for="(img, index) in merchantData.passportScanUrl"
                  :key="index"
                  class="image-container mr-2"
                >
                  <b-img :src="img" fluid rounded alt="CCCD Onboard" class="ekyc-img"></b-img>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Section 2: Histories -->
      <div
        v-for="(history, index) in merchantData.histories"
        :key="history.id"
        class="card mb-2 shadow-sm border-0"
        style="padding: 5px !important"
      >
        <div class="card-header bg-white border-0 p-0 mb-0" :id="'heading-' + index">
          <!-- Header for Accordion -->
          <div
            class="d-flex align-items-center justify-content-between px-3 py-2 bg-light rounded cursor-pointer"
            v-b-toggle="'collapse-' + index"
          >
            <div class="d-flex align-items-center">
              <div
                :class="[
                  'status-dot mr-3',
                  history?.statusKyc === 'ACTIVE' ? 'bg-success' : 'bg-danger',
                ]"
              ></div>
              <span class="font-weight-bold mr-2">{{ history.createdTime | formatDateTime }}</span>
              <!-- Connective dots logic can be CSS based, kept simple here -->
              <span class="text-muted mr-2 font-weight-bold">- - -</span>
              <span
                :class="
                  history?.statusKyc === 'ACTIVE'
                    ? 'text-success font-weight-bold'
                    : 'text-danger font-weight-bold'
                "
              >
                {{ history?.statusKyc === 'ACTIVE' ? 'Thành công' : 'Thất bại' }}
              </span>
              <div v-if="history?.message">
                <span class="text-muted mx-2 font-weight-bold">- - -</span>
                <span
                  :class="
                    history?.statusKyc === 'ACTIVE'
                      ? 'text-success font-weight-bold'
                      : 'text-danger font-weight-bold'
                  "
                >
                  {{ history.message }}
                </span>
              </div>
            </div>
            <div>
              <i class="bx bx-chevron-down f18"></i>
            </div>
          </div>
        </div>

        <b-collapse :id="'collapse-' + index" :visible="index === 0" role="tabpanel">
          <div class="card-body p-3">
            <b-row>
              <!-- Left: Info Comparison -->
              <b-col md="7" class="border-right">
                <h6 class="font-weight-bold mb-3 border-left-primary pl-2">Thông tin EKYC</h6>
                <div class="info-group">
                  <b-row class="mb-2">
                    <b-col cols="4" class="text-muted">Tên merchant</b-col>
                    <b-col cols="8" class="font-weight-medium">{{ history.fullName }}</b-col>
                  </b-row>
                  <b-row class="mb-2">
                    <b-col cols="4" class="text-muted">Số CCCD</b-col>
                    <b-col cols="8" class="font-weight-medium">{{ history.citizenIdentify }}</b-col>
                  </b-row>
                  <b-row class="mb-2">
                    <b-col cols="4" class="text-muted">Ngày sinh</b-col>
                    <b-col cols="8" class="font-weight-medium">{{ history.dateOfBirth }}</b-col>
                  </b-row>
                  <b-row class="mb-2">
                    <b-col cols="4" class="text-muted">Giới tính</b-col>
                    <b-col cols="8" class="font-weight-medium">{{ history.gender }}</b-col>
                  </b-row>
                  <b-row class="mb-2">
                    <b-col cols="4" class="text-muted">Địa chỉ thường trú</b-col>
                    <b-col cols="8" class="font-weight-medium">{{
                      history.placeOfResidence
                    }}</b-col>
                  </b-row>
                </div>
              </b-col>
              <!-- Right: OCR Images -->
              <b-col md="5">
                <h6 class="font-weight-bold mb-3 border-left-primary pl-2">Ảnh CCCD khi EKYC</h6>
                <div class="d-flex" v-viewer>
                  <div class="image-container mr-2" v-if="history.faceBase64">
                    <b-img
                      :src="getFaceImageSrc(history.faceBase64)"
                      fluid
                      rounded
                      class="ekyc-img"
                    ></b-img>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>
        </b-collapse>
      </div>
    </div>

    <!-- Approval Confirmation Modal -->
    <b-modal id="modal-approve" title="Xác nhận duyệt EKYC" centered hide-footer>
      <div>
        <p class="mb-3">Bạn có chắc chắn muốn duyệt EKYC cho merchant này?</p>
        <b-form-group label="Ghi chú (nếu có):">
          <b-form-textarea
            v-model="approvalNote"
            placeholder="Nhập ghi chú..."
            rows="4"
            max-rows="6"
          ></b-form-textarea>
        </b-form-group>
        <div class="d-flex justify-content-end mt-4">
          <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('modal-approve')"
            >Hủy</b-button
          >
          <b-button variant="success" @click="confirmApprove">Đồng ý</b-button>
        </div>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
  import moment from 'moment'
  import { MerchantService } from '@/services/merchant.service'
  import { mapGetters } from 'vuex'

  const defaultMerchantData = {
    mcId: '',
    mobile: '',
    email: '',
    customerName: '',
    merchantName: '',
    authoriserAddress1: '',
    gender: '',
    authoriserAddressPresent: '',
    birthDayTime: null,
    passport: '',
    passportScanUrl: [],
    statusKyc: '',
    messageKyc: null,
    histories: [],
  }

  export default {
    name: 'HistoryKycModal',
    props: {
      merchantId: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        loading: true,
        merchantData: { ...defaultMerchantData },

        approvalNote: '',
      }
    },
    filters: {
      formatDate(value) {
        if (!value) return ''
        return moment(value).format('DD/MM/YYYY')
      },
      formatDateTime(value) {
        if (!value) return ''
        return moment(value).format('HH:mm, DD/MM/YYYY')
      },
      formatGender(value) {
        if (value === 'MALE' || value === 'Nam') return 'Nam'
        if (value === 'FEMALE' || value === 'Nu') return 'Nữ'
        return value
      },
    },
    ...mapGetters({
      dataPermission: 'role/dataPermission',
    }),
    methods: {
      onModalShown() {
        if (this.merchantId) {
          this.fetchData()
        }
      },

      resetData() {
        this.merchantData = { ...defaultMerchantData }
        this.loading = true
        this.approvalNote = ''
      },
      async fetchData() {
        this.loading = true
        try {
          const response = await MerchantService.getHistoryKyc({ id: this.merchantId })
          if (response.success) {
            this.merchantData = response.data
          }
        } catch (e) {
          console.error(e)
        } finally {
          this.loading = false
        }
      },

      approveEkyc() {
        this.approvalNote = ''
        this.$bvModal.show('modal-approve')
      },
      async confirmApprove() {
        try {
          this.$bvModal.hide('modal-approve')
          this.loading = true
          const payload = {
            id: this.merchantData.mcId,
            note: this.approvalNote || '',
          }
          const response = await MerchantService.confirmKycMc(payload)
          if (response.success) {
            this.$bvToast.toast('Duyệt EKYC thành công', {
              title: 'Thành công',
              variant: 'success',
              solid: true,
            })
            this.$emit('approve', this.merchantData.mcId)
            this.$bvModal.hide('history-kyc-modal')
          } else {
            this.$bvToast.toast(response.message || 'Có lỗi xảy ra', {
              title: 'Thất bại',
              variant: 'danger',
              solid: true,
            })
          }
        } catch (e) {
          console.error(e)
          this.$bvToast.toast('Lỗi hệ thống', {
            title: 'Lỗi',
            variant: 'danger',
            solid: true,
          })
        } finally {
          setTimeout(() => {
            this.loading = false
          }, 1000)
        }
      },
      getFaceImageSrc(base64) {
        if (!base64) return ''
        if (base64.startsWith('http')) return base64
        if (base64.startsWith('data:image')) return base64
        return `data:image/jpeg;base64,${base64}`
      },
    },
  }
</script>

<style scoped>
  .border-left-primary {
    border-left: 4px solid #007bff;
    /* Bootstrap primary color */
  }

  .bg-light-gray {
    background-color: #f8f9fa;
  }

  .ekyc-img {
    height: 140px;
    width: 120px;
    object-fit: cover;
    cursor: pointer;
    border: 1px solid #ddd;
    transition: transform 0.2s;
  }

  .ekyc-img:hover {
    transform: scale(1.05);
  }

  .image-container {
    width: 120px;
    height: 140px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
  }

  .bg-light-danger {
    background-color: #fce4e4;
  }
</style>
